import type { TagDotProps, TagProps } from '../Tag'
import { Tag, TagDot } from '../Tag'
import type { components } from '@joyful/openapi/types.gen'
import { createEnumSchema } from '@joyful/utils'
import type { EnumValueMeta } from '@joyful/utils'

interface PracticeRequestTypeMetadata extends EnumValueMeta {
  dotVariant: NonNullable<TagDotProps['variant']>
}

export const PracticeRequestTypeSchema = createEnumSchema<
  components['schemas']['PracticeRequestType'],
  PracticeRequestTypeMetadata
>([
  {
    dotVariant: 'categoryBlue',
    title: `Eligibility / COB`,
    value: 'ELIGIBILITY_COB',
  },
  {
    dotVariant: 'categoryAmber',
    title: `Prior Authorization`,
    value: 'PRIOR_AUTHORIZATION',
  },
  {
    dotVariant: 'categoryEmerald',
    title: `Coding Guidance`,
    value: 'CODING_GUIDANCE',
  },
  {
    dotVariant: 'categorySky',
    title: `Clinical Documentation`,
    value: 'CLINICAL_DOCUMENTATION',
  },
  {
    dotVariant: 'categoryViolet',
    title: `Contracting / Credentialing`,
    value: 'CONTRACTING_CREDENTIALING',
  },
  {
    dotVariant: 'categoryRed',
    title: `Portal / System Access`,
    value: 'PORTAL_SYSTEM_ACCESS',
  },
  {
    dotVariant: 'categoryEmerald',
    title: `Payment Posting & Reconciliation`,
    value: 'PAYMENT_POSTING_RECONCILIATION',
  },
  {
    dotVariant: 'categorySky',
    title: `Claim / Patient Data Correction`,
    value: 'CLAIM_PATIENT_DATA_CORRECTION',
  },
  {
    dotVariant: 'categoryGray',
    title: `Uncollectible`,
    value: 'ACKNOWLEDGE_UNCOLLECTIBLE',
  },
  {
    dotVariant: 'categoryGray',
    title: `Acknowledge Patient Responsibility`,
    value: 'ACKNOWLEDGE_PATIENT_RESPONSIBILITY',
  },
])

type WritablePracticeRequestType = Exclude<
  components['schemas']['PracticeRequestType'],
  'ACKNOWLEDGE_UNCOLLECTIBLE'
>

function isWritablePracticeRequestType(
  requestType: components['schemas']['PracticeRequestType'],
): requestType is WritablePracticeRequestType {
  return requestType !== 'ACKNOWLEDGE_UNCOLLECTIBLE'
}

function isWritablePracticeRequestTypeDefinition(
  definition: (typeof PracticeRequestTypeSchema.definitions)[number],
): definition is (typeof PracticeRequestTypeSchema.definitions)[number] & {
  value: WritablePracticeRequestType
} {
  return isWritablePracticeRequestType(definition.value)
}

export function writablePracticeRequestTypeDefinitions() {
  return PracticeRequestTypeSchema.definitions.filter(
    isWritablePracticeRequestTypeDefinition,
  )
}

export const WritablePracticeRequestTypeSchema = createEnumSchema<
  WritablePracticeRequestType,
  PracticeRequestTypeMetadata
>(writablePracticeRequestTypeDefinitions())

export interface PracticeRequestTypeTagProps extends Partial<
  Omit<TagProps, 'children' | 'variant' | 'dot'>
> {
  requestType: components['schemas']['PracticeRequestType']
}

export function PracticeRequestTypeTag({
  requestType,
  ...tagProps
}: PracticeRequestTypeTagProps) {
  const metadata = PracticeRequestTypeSchema.meta(requestType)
  return (
    <Tag
      {...tagProps}
      dot={<TagDot variant={metadata.dotVariant} />}
      variant="plain"
    >
      {metadata.title}
    </Tag>
  )
}
